// Shared isometric-canvas math for the Workshop and the Map Editor — one
// source for the projection so the two canvases can never drift apart.
//
// The game's projection is 5:3, NOT classic 2:1 — the Unity Grid in
// MapGenerator.prefab has cellSize (1, 0.6), Border.cs places diamond
// corners at (±0.5, 0)/(0, ±0.3), and the tile art's top face measures
// 256x153.6. A tile diamond here is 120 x 72 world px at zoom 1 (same
// ratio, chosen so width, height and their halves are all integral).
//
// AXIS SIGN — the part that is easy to get wrong, and was wrong here until
// authored maps were found rendering mirrored against the client. Unity's
// isometric CellToWorld puts cell (a, b) at ((a - b) * 0.5, (a + b) * 0.3)
// with +Y UP; a canvas has +Y DOWN. So a growing (a + b) has to move a cell
// UP the canvas — towards NEGATIVE world Y here. That is the lone minus sign
// in gridCorner, and everything else in this file derives from it.
//
// Getting it wrong distorts nothing — every cell still lands on a perfect
// diamond and the map still fills the same area — it silently mirrors the
// scene top-to-bottom. Because that flip pins the left and right vertices in
// place it does not read as a mirror, and because the diamond maps onto
// itself it does not read as a rotation either, which is what made the
// original bug so hard to name. In cell terms it is a reflection across the
// anti-diagonal: cell (x, y) appears where (S-1-y, S-1-x) belongs.
//
// The sign also fixes DEPTH. With +Y up, a larger (a + b) is FARTHER from
// the camera, so painter's order runs from high (a + b) down to low — the
// reverse of what a y-down canvas wants. Both canvases sort that way; see
// the draw loop in mapCanvas.tsx and orderItems in workshop.view.tsx.
export const TILE_HALF_W = 60;
export const TILE_HALF_H = 36;

export const ZOOM_MIN = 0.1;
export const ZOOM_MAX = 16;
export const ZOOM_PER_PIXEL = 0.005;   // exp scale: ~200px drag doubles/halves
export const ZOOM_PER_WHEEL = 0.0015;  // ~one notch (deltaY 100) = ×1.16
// Trackpad pinch arrives as a wheel event with ctrlKey set and much smaller
// per-event deltas than a mouse notch, so it needs a larger factor to feel
// responsive. Tune this if pinch zooms too fast/slow on a Mac.
export const ZOOM_PER_PINCH = 0.01;

export interface ViewTransform {
    centerX: number;   // world point shown at the canvas centre
    centerY: number;
    zoom: number;
}

export const DEFAULT_VIEW: ViewTransform = {centerX: 0, centerY: 0, zoom: 1};

export function clampZoom(z: number): number {
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
}

// World position of grid INTERSECTION (a, b) — the corner where cells meet.
// Grid lines, the map border and viewport culling are all naturally expressed
// in intersections; only sprites and diamonds want cell centres. Both canvases
// used to inline this (and its inverse) separately, which is precisely how the
// axis sign came to be stated in four places and fixed in none.
export function gridCorner(a: number, b: number): [number, number] {
    return [(a - b) * TILE_HALF_W, -(a + b) * TILE_HALF_H];
}

// Inverse of gridCorner: a world point as fractional intersection coordinates.
export function gridAt(wx: number, wy: number): [number, number] {
    return [
        (wx / TILE_HALF_W - wy / TILE_HALF_H) / 2,
        (-wy / TILE_HALF_H - wx / TILE_HALF_W) / 2,
    ];
}

// Centre of cell (u, v): the midpoint of its four corners, which is the
// intersection half a cell along each axis.
export function tileCenter(u: number, v: number): [number, number] {
    return gridCorner(u + 0.5, v + 0.5);
}

// Inverse of tileCenter: the cell containing a world point.
export function snapToTile(wx: number, wy: number): {u: number; v: number} {
    const [a, b] = gridAt(wx, wy);
    return {u: Math.round(a - 0.5), v: Math.round(b - 0.5)};
}
